import React, {
  useState,
  useRef,
  useCallback,
  cloneElement,
  useId,
  useMemo,
  memo,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { usePortalPosition } from "@/hooks/ui";
import type { TooltipProps } from "./types";
import styles from "./Tooltip.module.css";

const OFFSET = 8;
const DEFAULT_AUTO_HIDE_DELAY = 5000;

/**
 * Tooltip component - Displays contextual information on hover.
 * Supports multiple positions and custom delay.
 * Uses React Portal for reliable z-index handling.
 *
 * WCAG AAA Compliant (WCAG 1.4.13 Content on Hover or Focus):
 * - Dismissible: Escape key hides tooltip without moving focus
 * - Hoverable: Users can hover over tooltip content without it disappearing
 * - Persistent: Content remains visible until hover/focus removed or dismissed
 * - aria-describedby links trigger to tooltip content
 */
export const Tooltip: React.FC<TooltipProps> = memo(
  ({
    content,
    children,
    position = "top",
    width = "auto",
    delay = 200,
    disabled = false,
    show,
    persistent = false,
    autoHideDelay = DEFAULT_AUTO_HIDE_DELAY,
    className,
    "data-testid": testId,
  }) => {
    const [internalVisible, setInternalVisible] = useState(false);

    // Use ref for hover tracking to avoid stale closures in setTimeout callbacks
    const isHoveringTooltipRef = useRef(false);

    // Track if tab was recently switched to prevent spurious focus-triggered tooltips
    // Bug #9000: Tooltips appearing spontaneously after browser tab switch
    const recentlyBecameVisibleRef = useRef(false);

    const timeoutRef = useRef<number | null>(null);
    const hideTimeoutRef = useRef<number | null>(null);
    const autoHideTimeoutRef = useRef<number | null>(null);

    const generatedId = useId();
    const tooltipId = testId || `tooltip-${generatedId}`;

    // Determine if controlled mode is active
    const isControlled = show !== undefined;

    // Helper to clear auto-hide timer
    const clearAutoHideTimer = useCallback(() => {
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current);
        autoHideTimeoutRef.current = null;
      }
    }, []);

    // Helper to start auto-hide timer
    const startAutoHideTimer = useCallback(() => {
      if (persistent || isControlled) return;
      clearAutoHideTimer();
      autoHideTimeoutRef.current = window.setTimeout(() => {
        setInternalVisible(false);
      }, autoHideDelay);
    }, [persistent, isControlled, autoHideDelay, clearAutoHideTimer]);

    // Calculate effective visibility
    const isVisible = useMemo(() => {
      if (!isControlled) {
        return internalVisible;
      }
      // Controlled mode: evaluate show prop
      if (typeof show === "function") {
        return show();
      }
      return show;
    }, [isControlled, show, internalVisible]);

    // Use shared hook for portal positioning
    const {
      triggerRef,
      elementRef: tooltipRef,
      coords,
    } = usePortalPosition({
      position,
      offset: OFFSET,
      isVisible,
    });

    // Auto-hide effect: start timer when tooltip becomes visible
    useEffect(() => {
      if (isVisible && !persistent && !isControlled) {
        startAutoHideTimer();
      } else {
        clearAutoHideTimer();
      }

      return () => {
        clearAutoHideTimer();
      };
    }, [
      isVisible,
      persistent,
      isControlled,
      startAutoHideTimer,
      clearAutoHideTimer,
    ]);

    // Bug #9000: Hide tooltip and prevent spurious focus events after tab switch
    useEffect(() => {
      const handleVisibilityChange = () => {
        if (document.hidden) {
          // Tab became hidden - hide any visible tooltip
          if (internalVisible && !isControlled) {
            setInternalVisible(false);
          }
          // Clear any pending show timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
        } else {
          // Tab became visible - set flag to ignore focus events briefly
          recentlyBecameVisibleRef.current = true;
          // Clear the flag after a short delay (enough to ignore spurious focus events)
          setTimeout(() => {
            recentlyBecameVisibleRef.current = false;
          }, 100);
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
      };
    }, [internalVisible, isControlled]);

    const showTooltip = useCallback(
      (fromFocus = false) => {
        if (disabled || isControlled) return;
        // Bug #9000: Ignore focus-triggered tooltips right after tab switch
        if (fromFocus && recentlyBecameVisibleRef.current) return;
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
        timeoutRef.current = window.setTimeout(() => {
          setInternalVisible(true);
        }, delay);
      },
      [delay, disabled, isControlled],
    );

    const hideTooltip = useCallback(
      (immediate = false) => {
        if (isControlled) return;
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (immediate) {
          setInternalVisible(false);
        } else {
          hideTimeoutRef.current = window.setTimeout(() => {
            // Use ref instead of state - always has current value, no stale closure
            if (!isHoveringTooltipRef.current) {
              setInternalVisible(false);
            }
          }, 100);
        }
      },
      [isControlled], // Remove isHoveringTooltip from dependencies - ref doesn't need it
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Escape" && isVisible && !isControlled) {
          e.preventDefault();
          hideTooltip(true);
        }
        children.props.onKeyDown?.(e);
      },
      [isVisible, hideTooltip, children.props, isControlled],
    );

    const handleTooltipMouseEnter = useCallback(() => {
      if (isControlled) return;
      isHoveringTooltipRef.current = true;
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      // Reset auto-hide timer when user hovers over tooltip content (WCAG 1.4.13)
      if (!persistent) {
        startAutoHideTimer();
      }
    }, [isControlled, persistent, startAutoHideTimer]);

    const handleTooltipMouseLeave = useCallback(() => {
      if (isControlled) return;
      isHoveringTooltipRef.current = false;
      hideTooltip(false);
    }, [hideTooltip, isControlled]);

    // Early return for disabled state
    if (disabled) {
      return children;
    }

    const trigger = cloneElement(children, {
      "aria-describedby": isVisible ? tooltipId : undefined,
      onMouseEnter: (e: React.MouseEvent) => {
        showTooltip();
        children.props.onMouseEnter?.(e);
      },
      onMouseLeave: (e: React.MouseEvent) => {
        hideTooltip(false);
        children.props.onMouseLeave?.(e);
      },
      onFocus: (e: React.FocusEvent) => {
        showTooltip(true); // fromFocus = true to detect tab switch spurious events
        children.props.onFocus?.(e);
      },
      onBlur: (e: React.FocusEvent) => {
        hideTooltip(false);
        children.props.onBlur?.(e);
      },
      onKeyDown: handleKeyDown,
    });

    const tooltipElement = (
      <div
        ref={tooltipRef}
        id={tooltipId}
        className={classNames(styles.tooltip, className)}
        data-position={position}
        data-width={width}
        data-visible={isVisible ? "true" : "false"}
        style={{
          position: "absolute",
          top: coords?.top ?? -9999,
          left: coords?.left ?? -9999,
          zIndex: 9999,
        }}
        role="tooltip"
        aria-hidden={!isVisible}
        onMouseEnter={handleTooltipMouseEnter}
        onMouseLeave={handleTooltipMouseLeave}
        data-testid={testId}
      >
        {content}
      </div>
    );

    return (
      <>
        <div ref={triggerRef} className={styles.triggerWrapper}>
          {trigger}
        </div>
        {createPortal(tooltipElement, document.body)}
      </>
    );
  },
);

Tooltip.displayName = "Tooltip";

export default Tooltip;
